import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface ComponentsProps {
  data: {
    compressor: {
      compressorType: string;
      inverterVfd: string;
      refrigerantCharge: string;
      oilType: string;
      oilCharge: string;
      compressorManufacturer: string;
      compressorPower: string;
      compressorQuantity: string;
    };
    waterSideHeatExchanger: {
      heatExchangerType: string;
      recommendedDeltaT: string;
      maxWorkingPressure: string;
      testPressure: string;
      maxWaterFlowRate: string;
      waterPressureDrop: string;
    };
    fanAirSideHeatExchange: {
      airSideHeatExchangerType: string;
      airDischargeType: string;
      fanInverterVfd: string;
      fanOutputPower: string;
      fanQuantity: string;
      totalAirflow: string;
    };
    waterPump: {
      manufacturer: string;
      model: string;
      powerConsumption: string;
      pressure: string;
      flowRate: string;
    };
  };
  onChange: (subSection: string, field: string, value: string) => void;
}

const ComponentsSection: React.FC<ComponentsProps> = ({ data, onChange }) => {
  const yesNoOptions = ['Y', 'N'];
  const compressorTypes = ['Scroll', 'Reciprocating', 'Screw', 'Rotary', 'Other'];
  const heatExchangerTypes = ['Flat Plate', 'Shell & Tube', 'Coaxial', 'Other'];
  const airHeatExchangerTypes = ['Tube-Fin', 'Microchannnel', 'Other'];
  const airDischargeTypes = ['Horizontal', 'Vertical'];

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h5">3. Components</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" paragraph>
            If the unit does not have a primary water pump, leave the section blank. Examples are given for some properties.
          </Typography>
          <Typography variant="body2" paragraph>
            If there are two different refrigerants, give separate values for each refrigerant, with the low-temperature refrigerant first, separated by a slash. For example, if there are two R32 circuits with 2.5 kg each and two R515B circuits with 4 kg each, enter "5/8" in the refrigerant charge section.
          </Typography>
          <Typography variant="body2" paragraph>
            See Appendix A for illustrations of "vertical" and "horizontal" air discharge types.
          </Typography>
        </Box>

        {/* Compressor Section */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Compressor</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" className="blue-highlight">
                <InputLabel id="compressor-type-label">Compressor Type</InputLabel>
                <Select
                  labelId="compressor-type-label"
                  value={data.compressor.compressorType}
                  label="Compressor Type"
                  onChange={(e) => onChange('compressor', 'compressorType', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select type</em>
                  </MenuItem>
                  {compressorTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" className="blue-highlight">
                <InputLabel id="inverter-vfd-label">Inverter/VFD</InputLabel>
                <Select
                  labelId="inverter-vfd-label"
                  value={data.compressor.inverterVfd}
                  label="Inverter/VFD"
                  onChange={(e) => onChange('compressor', 'inverterVfd', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Refrigerant Charge (total)"
                value={data.compressor.refrigerantCharge}
                onChange={(e) => onChange('compressor', 'refrigerantCharge', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[kg] / [kg]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Oil Type"
                value={data.compressor.oilType}
                onChange={(e) => onChange('compressor', 'oilType', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Oil Charge"
                value={data.compressor.oilCharge}
                onChange={(e) => onChange('compressor', 'oilCharge', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[kg]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Compressor Manufacturer"
                value={data.compressor.compressorManufacturer}
                onChange={(e) => onChange('compressor', 'compressorManufacturer', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Compressor Power (rated output power)"
                value={data.compressor.compressorPower}
                onChange={(e) => onChange('compressor', 'compressorPower', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[kW]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Compressor Quantity"
                value={data.compressor.compressorQuantity}
                onChange={(e) => onChange('compressor', 'compressorQuantity', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                type="number"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Water Side Heat Exchanger Section */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Water Side Heat Exchanger</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" className="blue-highlight">
                <InputLabel id="heat-exchanger-type-label">Heat Exchanger Type</InputLabel>
                <Select
                  labelId="heat-exchanger-type-label"
                  value={data.waterSideHeatExchanger.heatExchangerType}
                  label="Heat Exchanger Type"
                  onChange={(e) => onChange('waterSideHeatExchanger', 'heatExchangerType', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select type</em>
                  </MenuItem>
                  {heatExchangerTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Recommended ΔT"
                value={data.waterSideHeatExchanger.recommendedDeltaT}
                onChange={(e) => onChange('waterSideHeatExchanger', 'recommendedDeltaT', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[K]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Max Working Pressure"
                value={data.waterSideHeatExchanger.maxWorkingPressure}
                onChange={(e) => onChange('waterSideHeatExchanger', 'maxWorkingPressure', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[kPa]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Test Pressure"
                value={data.waterSideHeatExchanger.testPressure}
                onChange={(e) => onChange('waterSideHeatExchanger', 'testPressure', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[kPa]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Max Water Flow Rate"
                value={data.waterSideHeatExchanger.maxWaterFlowRate}
                onChange={(e) => onChange('waterSideHeatExchanger', 'maxWaterFlowRate', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[L/hr]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Water Pressure Drop"
                value={data.waterSideHeatExchanger.waterPressureDrop}
                onChange={(e) => onChange('waterSideHeatExchanger', 'waterPressureDrop', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[kPa]"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Fan/Air Side Heat Exchange */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Fan/Air Side Heat Exchange</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" className="blue-highlight">
                <InputLabel id="air-side-heat-exchanger-label">Air Side Heat Exchanger Type</InputLabel>
                <Select
                  labelId="air-side-heat-exchanger-label"
                  value={data.fanAirSideHeatExchange.airSideHeatExchangerType}
                  label="Air Side Heat Exchanger Type"
                  onChange={(e) => onChange('fanAirSideHeatExchange', 'airSideHeatExchangerType', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select type</em>
                  </MenuItem>
                  {airHeatExchangerTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" className="blue-highlight">
                <InputLabel id="air-discharge-type-label">Air Discharge Type</InputLabel>
                <Select
                  labelId="air-discharge-type-label"
                  value={data.fanAirSideHeatExchange.airDischargeType}
                  label="Air Discharge Type"
                  onChange={(e) => onChange('fanAirSideHeatExchange', 'airDischargeType', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select type</em>
                  </MenuItem>
                  {airDischargeTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" className="blue-highlight">
                <InputLabel id="fan-inverter-vfd-label">Fan Inverter/VFD</InputLabel>
                <Select
                  labelId="fan-inverter-vfd-label"
                  value={data.fanAirSideHeatExchange.fanInverterVfd}
                  label="Fan Inverter/VFD"
                  onChange={(e) => onChange('fanAirSideHeatExchange', 'fanInverterVfd', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {yesNoOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fan Output Power (each)"
                value={data.fanAirSideHeatExchange.fanOutputPower}
                onChange={(e) => onChange('fanAirSideHeatExchange', 'fanOutputPower', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[W]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fan Quantity"
                value={data.fanAirSideHeatExchange.fanQuantity}
                onChange={(e) => onChange('fanAirSideHeatExchange', 'fanQuantity', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                type="number"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Total Airflow"
                value={data.fanAirSideHeatExchange.totalAirflow}
                onChange={(e) => onChange('fanAirSideHeatExchange', 'totalAirflow', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[m³/hr]"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Water Pump */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Water Pump</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Manufacturer"
                value={data.waterPump.manufacturer}
                onChange={(e) => onChange('waterPump', 'manufacturer', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="eg: proprietary/in-house/Grundfos"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Model"
                value={data.waterPump.model}
                onChange={(e) => onChange('waterPump', 'model', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Power Consumption"
                value={data.waterPump.powerConsumption}
                onChange={(e) => onChange('waterPump', 'powerConsumption', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[W]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Pressure"
                value={data.waterPump.pressure}
                onChange={(e) => onChange('waterPump', 'pressure', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[kPa]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Flow rate at specified pressure"
                value={data.waterPump.flowRate}
                onChange={(e) => onChange('waterPump', 'flowRate', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[L/s]"
              />
            </Grid>
          </Grid>
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
};

export default ComponentsSection;